/**
 * What can be pinned, what each thing means, and what is asked about it.
 *
 * This list is the product decision of the whole tool. Everything else is
 * plumbing: if the vocabulary is wrong, three hundred points come back wrongly
 * labelled and fixing them means walking the Camino again.
 *
 * ## Why the list is closed, and why the kind is chosen before the photo
 *
 * Typing a label while walking, one-handed, in the sun, gives you "cruce",
 * "cruce raro" and "ojo aqui" as three names for one thing, and then nothing
 * can be counted, filtered or drawn on a map. A closed list is one tap and
 * always the same word.
 *
 * Choosing it **first** buys three things a label added afterwards cannot: the
 * app knows which questions to ask, it can say how the photo should be framed,
 * and the point is already stored before the phone leaves your hand.
 *
 * ## Where the list came from
 *
 * Not guessed. It follows what the map project this feeds already knows is
 * missing, and where each kind of gap actually gets filled from:
 *
 * - **Milestones, arrows, junctions and fountains**: nobody has a database of
 *   these at all — the pipeline that already contrasts kilometres against our
 *   own map is what this app was built to feed.
 * - **Accommodation, bars and pharmacies**: the official tourism registries
 *   give the skeleton — name, position, sometimes a phone — and stop there.
 *   Price, whether it stamps the credential, opening hours and whether a dog
 *   is welcome are in **no registry of any region**, and are exactly what a
 *   person standing there can answer in one tap.
 * - **What is beside the trail** — wayside crosses, viewpoints, springs,
 *   archaeological sites, picnic areas — is already mapped by OpenStreetMap in
 *   the thousands and never shown to anybody walking past one.
 *
 * ## Why each tag carries its own questions, and what "otro" means here
 *
 * "Una fuente" is a photograph. "Una fuente seca" is something the map can act
 * on. The same thing written as a note is prose a person has to read one by
 * one, and nothing downstream can do anything with it.
 *
 * Every closed question still keeps an escape: whenever `options` includes
 * `"otro"`, picking it opens a one-line box right there for whatever the list
 * did not anticipate. It is saved against that question, `<id>_otro`, instead
 * of dumped into the point's general note — so "qué es -> otro -> cruceiro de
 * madera con una fecha de 1734" stays attached to the question it answers.
 */

/** How much a photo matters for this kind of point. */
export type PhotoNeed = "required" | "suggested" | "optional";

/** A question asked about a point, once its kind is known. */
export type Field =
  | {
      id: string;
      label: string;
      kind: "choice";
      /**
       * One tap each. Tapping the chosen one again clears it.
       *
       * Ending the list with `"otro"` is what turns on the free-text box: see
       * the module comment above.
       */
      options: readonly string[];
    }
  | {
      id: string;
      label: string;
      kind: "number";
      unit: string;
      hint: string;
    };

/**
 * How the growing list of tags is grouped on screen.
 *
 * Four short sections read faster than one long grid, and match how the walk
 * actually splits: staying on the trail, stopping somewhere, the village's own
 * services, and what might spoil the day.
 */
export type Category = "trazado" | "parar" | "servicios" | "avisos";

export const CATEGORIES: ReadonlyArray<{ id: Category; label: string }> = [
  { id: "trazado", label: "El trazado" },
  { id: "parar", label: "Para parar" },
  { id: "servicios", label: "Servicios del pueblo" },
  { id: "avisos", label: "Avisos" },
];

export interface Tag {
  /** Stable key. Goes into the export and into the map. Never translated. */
  id: string;
  emoji: string;
  /** What the button says. */
  label: string;
  /** One line under the button: what counts as this and what does not. */
  hint: string;
  category: Category;
  photo: PhotoNeed;
  /**
   * How much a photo of the credential stamp matters — absent for the nine
   * tags that have nothing to stamp.
   *
   * Independent of `photo`: a mojón and a stamp are two different things
   * worth two different verdicts. Where a place puts a distinctive stamp on
   * the credential, that stamp is often the actual reason to remember the
   * place at all, so it does not just inherit whatever `photo` says.
   */
  sello?: PhotoNeed;
  /** How to frame the photo, shown next to the camera button. */
  framing: string;
  /**
   * Whether what is recorded goes stale on its own.
   *
   * A milestone stays put for decades; a fallen bridge gets rebuilt and a
   * puddle dries up. Showing a 2026 warning in 2029 is worse than showing
   * nothing: it is the kind of false certainty that gets somebody walking in
   * the dark. Perishable points carry their date into the export so whoever
   * publishes them has to decide when they expire.
   */
  perishable: boolean;
  /** What gets asked about this kind of point. Empty when nothing does. */
  fields: readonly Field[];
}

/** A closed question everywhere it recurs: sí, no, or honestly not sure. */
const SI_NO_NO_SE = ["sí", "no", "no lo sé"] as const;

/**
 * Whether an amenity exists, and whether it costs anything — in one tap.
 *
 * The exact price is the "detail that makes the difference" a rival app
 * collects by hand for every accommodation, but asking for a euro figure on
 * five separate amenities turns one stop into a chore. This gets the part
 * that changes what a pilgrim decides — free, paid, or not there — and
 * leaves the loose number, if known, for the note.
 */
const DISPONIBLE = ["no", "gratis", "de pago"] as const;

export const TAGS: readonly Tag[] = [
  // ---------------------------------------------------------- el trazado --
  {
    id: "mojon",
    emoji: "🪨",
    label: "Mojón",
    hint: "La piedra con los kilómetros.",
    category: "trazado",
    photo: "required",
    framing: "De frente y cerca, que se lea la placa.",
    perishable: false,
    fields: [
      {
        id: "km_restantes",
        label: "Kilómetros que faltan, según la piedra",
        kind: "number",
        unit: "km",
        // The number carved on the stone: what it says is left to Santiago.
        // It is a fact about the object in front of you, like whether it is
        // broken, and it is written nowhere else in a form anything can read.
        hint: "El número grabado, con los decimales que traiga.",
      },
      {
        id: "estado",
        label: "Cómo está",
        kind: "choice",
        options: ["entera", "rota", "pintada", "tapada"],
      },
    ],
  },
  {
    id: "flecha",
    emoji: "➡️",
    label: "Flecha",
    hint: "Flecha amarilla o señal. Importa sobre todo cuando falta.",
    category: "trazado",
    photo: "suggested",
    framing: "Desde donde la verías al llegar.",
    perishable: false,
    fields: [
      {
        id: "estado",
        label: "Cómo se ve",
        kind: "choice",
        options: ["clara", "borrada", "confusa", "no hay"],
      },
    ],
  },
  {
    id: "cruce",
    emoji: "❓",
    label: "Cruce dudoso",
    hint: "Donde es fácil equivocarse.",
    category: "trazado",
    photo: "required",
    framing: "Mirando hacia donde hay que ir.",
    perishable: false,
    fields: [
      {
        id: "hacia",
        label: "Por dónde se sigue",
        kind: "choice",
        options: ["izquierda", "derecha", "recto", "atrás"],
      },
      {
        id: "por_que",
        label: "Por qué se falla",
        kind: "choice",
        options: ["no hay señal", "señal escondida", "dos caminos iguales", "señal engañosa"],
      },
    ],
  },
  // ------------------------------------------------------------ para parar --
  {
    id: "fuente",
    emoji: "💧",
    label: "Fuente",
    hint: "Agua para beber. Es lo que no enseña nadie.",
    category: "parar",
    photo: "suggested",
    framing: "El caño y el cartel, si lo tiene.",
    perishable: false,
    fields: [
      { id: "estado", label: "Cómo está", kind: "choice", options: ["mana", "poca", "seca"] },
      {
        id: "potable",
        label: "¿Se puede beber?",
        kind: "choice",
        options: ["sí", "dice que no", "no lo pone"],
      },
    ],
  },
  {
    id: "parada",
    emoji: "⭐",
    label: "Merece parar",
    hint: "Un cruceiro, un mirador, un yacimiento, algo que contar.",
    category: "parar",
    photo: "required",
    framing: "Entero y desde donde luce.",
    perishable: false,
    fields: [
      {
        // Matches what the map already carries by the thousand and shows to
        // nobody: cruceiros, miradores, yacimientos, manantiales, áreas de
        // descanso. Naming the same categories means these points can be
        // checked against that pile instead of starting a new one.
        id: "que_es",
        label: "Qué es",
        kind: "choice",
        options: [
          "cruceiro",
          "mirador",
          "yacimiento",
          "manantial",
          "ermita",
          "área de descanso",
          "otro",
        ],
      },
    ],
  },
  {
    id: "sello",
    emoji: "🔖",
    label: "Sellan aquí",
    hint: "Quién pone el sello de la credencial. No consta en ningún sitio.",
    category: "parar",
    photo: "optional",
    framing: "El cartel o la puerta, para reconocerlo.",
    perishable: false,
    fields: [
      {
        id: "quien",
        label: "Quién lo pone",
        kind: "choice",
        options: ["albergue", "bar", "iglesia", "ayuntamiento", "tienda", "otro"],
      },
      {
        id: "gratis",
        label: "¿Cobran?",
        kind: "choice",
        options: ["gratis", "hay que consumir", "cobran"],
      },
    ],
  },
  // --------------------------------------------------- servicios del pueblo --
  {
    id: "alojamiento",
    emoji: "🛏️",
    label: "Alojamiento",
    hint: "Albergue, hostal, casa rural, hotel, camping.",
    category: "servicios",
    // Confirmed against the project's own inventory of what the official
    // registries hold: a photo of a place to sleep exists in 0.2% of them.
    // It is the single most valuable thing this tag can bring back.
    photo: "required",
    // "Si sella la credencial" is nowhere in any registry either, and unlike
    // "hay wifi" it is not something a phone call could confirm later: the
    // stamp's own design has to be seen. Required, not suggested.
    sello: "required",
    framing: "La fachada o el cartel, que se reconozca.",
    perishable: false,
    fields: [
      {
        id: "tipo",
        label: "Qué tipo es",
        kind: "choice",
        options: ["albergue", "pensión o hostal", "casa rural", "hotel", "camping", "otro"],
      },
      {
        // "No existe el campo en ningún registro oficial", word for word from
        // the project's own research — and the one a pilgrim checks first.
        id: "precio",
        label: "Precio de la cama",
        kind: "number",
        unit: "€",
        hint: "O de la habitación, si no es un albergue. Si dudas, pregunta.",
      },
      {
        // Not a daily opening hour — that one changes rarely and rarely
        // matters. This is the one no register has at all: whether the place
        // is open to arrive at, this year, before the pilgrim walks there.
        id: "cuando_cierra",
        label: "¿Hasta cuándo abre?",
        kind: "choice",
        options: ["todo el año", "cierra en invierno", "abre solo en verano", "otro"],
      },
      {
        id: "hora_salida",
        label: "Hora de salida",
        kind: "choice",
        options: ["8:00", "9:00", "10:00", "11:00", "otro"],
      },
      { id: "sella", label: "¿Sella la credencial?", kind: "choice", options: SI_NO_NO_SE },
      { id: "admite_perros", label: "¿Admite perros?", kind: "choice", options: SI_NO_NO_SE },
      // The five with a loose price attached — "el detalle que marca la
      // diferencia", según el propio análisis de la competencia: lavadora 3 €,
      // secadora 3 €… Aquí no hace falta el número exacto, solo si existe y si
      // cuesta; el precio suelto, si lo sabes, cabe en la nota.
      { id: "lavadora", label: "Lavadora", kind: "choice", options: DISPONIBLE },
      { id: "secadora", label: "Secadora", kind: "choice", options: DISPONIBLE },
      { id: "cocina", label: "Cocina para cocinar", kind: "choice", options: DISPONIBLE },
      { id: "cena", label: "Cena", kind: "choice", options: DISPONIBLE },
      { id: "desayuno", label: "Desayuno", kind: "choice", options: DISPONIBLE },
    ],
  },
  {
    id: "restaurante",
    emoji: "🍴",
    label: "Bar o restaurante",
    hint: "Dónde comer o tomar algo.",
    category: "servicios",
    photo: "suggested",
    // Suggested, not required: most bars and cafés never stamp anything, and
    // the ones that do are the exception worth a photo, not the rule.
    sello: "suggested",
    framing: "El cartel, o la carta si tiene menú del peregrino.",
    perishable: false,
    fields: [
      {
        id: "menu_peregrino",
        label: "¿Tiene menú del peregrino?",
        kind: "choice",
        options: SI_NO_NO_SE,
      },
      {
        id: "precio_menu",
        label: "Precio del menú",
        kind: "number",
        unit: "€",
        hint: "Solo si tiene menú del peregrino.",
      },
      { id: "sella", label: "¿Sella la credencial?", kind: "choice", options: SI_NO_NO_SE },
      { id: "admite_perros", label: "¿Admite perros?", kind: "choice", options: SI_NO_NO_SE },
    ],
  },
  {
    id: "farmacia",
    emoji: "💊",
    label: "Farmacia",
    hint: "Con la cruz verde.",
    category: "servicios",
    photo: "optional",
    framing: "El cartel de la cruz verde.",
    perishable: false,
    fields: [
      {
        id: "abierta",
        label: "¿Estaba abierta?",
        kind: "choice",
        options: ["abierta ahora", "cerrada ahora", "no lo sé"],
      },
    ],
  },
  {
    id: "turismo",
    emoji: "ℹ️",
    label: "Oficina de turismo",
    hint: "Información turística.",
    category: "servicios",
    photo: "optional",
    framing: "La entrada o el cartel.",
    perishable: false,
    fields: [
      {
        id: "abierta",
        label: "¿Estaba abierta?",
        kind: "choice",
        options: ["abierta ahora", "cerrada ahora", "no lo sé"],
      },
    ],
  },
  {
    id: "iglesia",
    emoji: "⛪",
    label: "Iglesia",
    hint: "La del pueblo, si tiene algo que contar o algo que sellar.",
    category: "servicios",
    photo: "required",
    // Churches are one of the classic places a peregrino specifically seeks
    // out a stamp, and each parish's own is often the whole reason to
    // remember it. Required, same as alojamiento.
    sello: "required",
    framing: "La fachada, o el retablo si entras.",
    perishable: false,
    fields: [
      {
        id: "abierta",
        label: "¿Estaba abierta?",
        kind: "choice",
        options: ["abierta ahora", "cerrada ahora", "no lo sé"],
      },
      { id: "sella", label: "¿Sella la credencial?", kind: "choice", options: SI_NO_NO_SE },
    ],
  },
  {
    id: "pueblo",
    emoji: "🏘️",
    label: "Pueblo",
    hint: "Algo del sitio por el que pasas: una plaza, una vista, lo que sea.",
    category: "servicios",
    photo: "suggested",
    framing: "Lo más representativo que veas.",
    perishable: false,
    fields: [],
  },
  // ------------------------------------------------------------- avisos --
  {
    id: "aviso",
    emoji: "⚠️",
    label: "Aviso",
    hint: "Algo que puede fastidiarte el día.",
    category: "avisos",
    photo: "suggested",
    framing: "Lo justo para que se entienda de qué hablas.",
    perishable: true,
    fields: [
      {
        id: "tipo",
        label: "Qué pasa",
        kind: "choice",
        options: [
          "perro suelto",
          "camino cortado",
          "desvío peligroso",
          "barro",
          "carretera sin arcén",
          "obras",
          "otro",
        ],
      },
      {
        // Without this, every warning would have to be treated as expiring
        // next week, and the ones that matter for years — a stretch of road
        // with no verge — would get thrown out along with the puddles.
        id: "dura",
        label: "¿Cuánto va a durar?",
        kind: "choice",
        options: ["días", "semanas", "meses", "es así siempre", "no lo sé"],
      },
    ],
  },
  {
    id: "sin_agua",
    emoji: "☀️",
    label: "Sin sombra ni agua",
    hint: "Donde empieza un tramo largo al descubierto.",
    category: "avisos",
    photo: "optional",
    framing: "El tramo que viene, si se aprecia.",
    perishable: false,
    fields: [
      {
        id: "km_sin_nada",
        label: "Cuánto dura",
        kind: "number",
        unit: "km",
        hint: "A ojo vale. Es un aviso, no una medición.",
      },
      {
        id: "falta",
        label: "Qué falta",
        kind: "choice",
        options: ["sombra", "agua", "las dos cosas"],
      },
    ],
  },
];

/** The tag with that id, or `null` if it is not one of ours. */
export function tagById(id: string): Tag | null {
  return TAGS.find((tag) => tag.id === id) ?? null;
}

/** All tags belonging to a category, in the order they are declared. */
export function tagsIn(category: Category): Tag[] {
  return TAGS.filter((tag) => tag.category === category);
}

/**
 * Whether a choice field offers the free-text escape hatch.
 *
 * A field opts in simply by ending its list with `"otro"` — no separate flag
 * to keep in sync with the options themselves.
 */
export function hasOther(field: Field): boolean {
  return field.kind === "choice" && field.options.includes("otro");
}

/** The key the free-text detail for `field.id -> "otro"` is stored under. */
export function otherKey(fieldId: string): string {
  return `${fieldId}_otro`;
}

/**
 * Reads a number the way it actually gets written on a milestone or a menu.
 *
 * Spanish keyboards offer a comma for the decimal point — "545,213", "12,50
 * €" — and a thumb on a phone produces whichever separator that keyboard
 * happens to have. Both mean the same thing, and refusing one of them would
 * lose the field its whole point. Anything that is not a plain number gives
 * `null`, rather than a silent zero that would look like a real measurement.
 */
export function readNumber(text: string): number | null {
  const cleaned = text.trim().replace(",", ".");
  if (cleaned === "") return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}
